/**
 * Decide held units (batch 8 T12c2, #3033): accept or reject some or all of the units a receipt put on hold.
 *
 * Nothing is decided automatically and nothing is defaulted: the user picks accept or reject, a quantity of at most the units still
 * held, an explicit reason when rejecting, and confirms a summary. Accepting moves the units into stock at the server's price (shown
 * nowhere here); rejecting adds no stock. The write is a keyed command with one slot per receipt line, so a double click or a retry
 * after a lost answer can never decide the same units twice. A replay shows the CURRENT counters, not those after the first call.
 *
 * Standalone: it takes the receipt line as props and enforces the owner/target boundary itself.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/hooks/useNotify';
import { useReceivingOperations, type SubmitResult } from '@/hooks/usePurchaseReceipts';
import { emptyHoldDraft, evaluateHold, type HoldDraft } from './receivingArithmetic';
import { slotOf } from './receivingJournal';
import { blockedMessage, JournalNotice, PendingOperationAlert, ReceivingBoundary, ReceivingErrorAlert } from './receivingParts';
import { NOTE_MAX, REJECT_REASONS, type ResolveHoldResult } from './receivingTypes';

/** The held receipt line a decision is about. */
export interface HoldTarget {
  /** The receipt line (not the order line). */
  readonly receiptLineId: string;
  readonly receiptNumber: string;
  readonly partNumber: string;
  readonly partName: string;
  /** Units of the line still held, as the history last showed them. */
  readonly quantityHeld: number;
}

export interface ResolveHoldDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrderId: string;
  target: HoldTarget;
  onResolved?: ((result: ResolveHoldResult) => void) | undefined;
}

export function ResolveHoldDialog(props: ResolveHoldDialogProps) {
  // The receipt line is part of the target: another line is another dialog.
  return (
    <ReceivingBoundary target={`${props.purchaseOrderId}:${props.target.receiptLineId}`} dialog={{ open: props.open, onClose: props.onClose }}>
      <ResolveHoldForm {...props} />
    </ReceivingBoundary>
  );
}

type Stage = 'edit' | 'review' | 'done';

function ResolveHoldForm({ onClose, purchaseOrderId, target, onResolved }: ResolveHoldDialogProps) {
  const { t } = useTranslation('procurementReceiving');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const ops = useReceivingOperations(purchaseOrderId);
  const [draft, setDraft] = useState<HoldDraft>(emptyHoldDraft());
  const [stage, setStage] = useState<Stage>('edit');
  const [reviewed, setReviewed] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [result, setResult] = useState<ResolveHoldResult | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const sending = useRef(false);

  const { problems, body } = evaluateHold(draft, target.quantityHeld);
  const signature = body ? JSON.stringify(body) : null;
  const reviewing = stage === 'review' && signature !== null && signature === reviewed;
  const shown: Stage = stage === 'review' && !reviewing ? 'edit' : stage;

  const slot = slotOf('resolve_hold', target.receiptLineId);
  const held = ops.entries.find((entry) => entry.slot === slot) ?? null;
  const pending = ops.isPending(slot);
  const partLabel = `${target.partNumber} ${target.partName}`.trim();

  const update = (patch: Partial<HoldDraft>) => {
    setBlocked(null);
    setRefusal(null);
    setDraft((current) => ({ ...current, ...patch }));
  };

  const handle = (outcome: SubmitResult) => {
    if (outcome.status === 'success') {
      const decided = outcome.result as ResolveHoldResult;
      setResult(decided);
      setStage('done');
      setRefusal(null);
      notify.success(t('hold.notifyTitle'), t('hold.notifyBody', { part: partLabel }));
      onResolved?.(decided);
    } else if (outcome.status === 'refused') {
      setRefusal(outcome.error);
      setStage('edit');
    } else if (outcome.status === 'blocked') {
      setBlocked(blockedMessage(outcome.reason, t as never));
      setStage('edit');
    } else if (outcome.status === 'uncertain') {
      setStage('edit');
    }
  };

  const review = () => {
    setAttempted(true);
    if (signature === null) return;
    setReviewed(signature);
    setStage('review');
  };

  const confirm = async () => {
    if (!reviewing || body === null || sending.current) return;
    sending.current = true;
    try {
      handle(await ops.resolveHold(target.receiptLineId, body));
    } finally {
      sending.current = false;
    }
  };

  const retry = async () => {
    if (held === null || sending.current) return;
    sending.current = true;
    try {
      handle(await ops.retry(held));
    } finally {
      sending.current = false;
    }
  };

  const title = t('hold.title', { part: partLabel });
  const storageOk = ops.journalStatus === 'ok';

  return (
    <ResponsiveDialog open onClose={onClose} fullWidth maxWidth="sm" mobileTitle={title} aria-labelledby="resolve-hold-title">
      <DialogTitle id="resolve-hold-title">{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <JournalNotice status={ops.journalStatus} onDiscardDamaged={ops.discardDamaged} onRecheck={ops.recheckStorage} />
          {!ops.canReceive && <Alert severity="warning">{t('errors.noPermission')}</Alert>}
          <Typography variant="body2" color="text.secondary">
            {t('hold.context', { receipt: target.receiptNumber, count: target.quantityHeld })}
          </Typography>

          {shown === 'done' && result !== null && (
            <Stack spacing={1}>
              <Alert severity="success">{t(result.decision === 'accept' ? 'hold.done.accepted' : 'hold.done.rejected', { count: result.quantity })}</Alert>
              {result.replayed && <Alert severity="info">{t('hold.done.replayed')}</Alert>}
              <Typography variant="body2">
                {t('hold.done.counters', { accepted: result.receiptLine.accepted, rejected: result.receiptLine.rejected, held: result.receiptLine.held })}
              </Typography>
              <Typography variant="body2">{result.fullyReceived ? t('receive.done.fullyReceived') : t('receive.done.notFullyReceived')}</Typography>
            </Stack>
          )}

          {shown !== 'done' && held !== null && (
            <PendingOperationAlert
              entry={held}
              error={ops.errorOf(slot)}
              busy={pending}
              journalStatus={ops.journalStatus}
              onRetry={() => void retry()}
              onDiscard={() => ops.discard(held)}
            />
          )}

          {shown === 'edit' && held === null && (
            <Stack spacing={2}>
              <FormControl error={attempted && problems.includes('decisionRequired')}>
                <FormLabel id="hold-decision">{t('hold.decision.label')}</FormLabel>
                <RadioGroup
                  aria-labelledby="hold-decision"
                  value={draft.decision}
                  onChange={(event) => update({ decision: event.target.value as HoldDraft['decision'], ...(event.target.value === 'accept' ? { reason: '' as const } : {}) })}
                >
                  <FormControlLabel value="accept" control={<Radio />} label={t('hold.decision.accept')} />
                  <FormControlLabel value="reject" control={<Radio />} label={t('hold.decision.reject')} />
                </RadioGroup>
              </FormControl>
              <TextField
                size="small"
                label={t('hold.quantity')}
                value={draft.quantity}
                onChange={(event) => update({ quantity: event.target.value })}
                error={attempted && (problems.includes('quantityInvalid') || problems.includes('quantityExceedsHeld'))}
                helperText={t('hold.quantityHint', { count: target.quantityHeld })}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                sx={{ maxWidth: 220 }}
              />
              {draft.decision === 'reject' && (
                <TextField
                  select
                  size="small"
                  required
                  label={t('hold.reason')}
                  value={draft.reason}
                  onChange={(event) => update({ reason: event.target.value as HoldDraft['reason'] })}
                  error={attempted && problems.includes('reasonRequired')}
                >
                  {REJECT_REASONS.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {t(`rejectReason.${reason}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                size="small"
                multiline
                minRows={2}
                label={t('hold.note')}
                value={draft.note}
                onChange={(event) => update({ note: event.target.value })}
                slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
              />
              {attempted &&
                problems
                  .filter((problem) => problem !== 'noteTooLong')
                  .map((problem) => (
                    <Typography key={problem} variant="caption" color="error" role="alert">
                      {t(`hold.problems.${problem}`, { count: target.quantityHeld })}
                    </Typography>
                  ))}
            </Stack>
          )}

          {shown === 'review' && held === null && body !== null && (
            <Stack spacing={1}>
              <Alert severity={body.decision === 'reject' ? 'warning' : 'info'}>
                <strong>{t('hold.summary.title')}</strong>{' '}
                {t(body.decision === 'accept' ? 'hold.summary.accept' : 'hold.summary.reject', { count: body.quantity, part: partLabel })}
              </Alert>
              {body.decision === 'reject' && body.rejectReason !== undefined && (
                <Typography variant="body2">{t('hold.summary.reason', { reason: t(`rejectReason.${body.rejectReason}`) })}</Typography>
              )}
              {body.note !== undefined && <Typography variant="body2">{t('hold.summary.note', { note: body.note })}</Typography>}
              <Typography variant="body2" color="text.secondary">
                {t(body.decision === 'accept' ? 'hold.summary.acceptEffect' : 'hold.summary.rejectEffect')}
              </Typography>
            </Stack>
          )}

          <ReceivingErrorAlert error={refusal} />
          {blocked !== null && <Alert severity="error">{blocked}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {shown === 'done' ? (
          <Button onClick={onClose} variant="contained">
            {tc('close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>{tc('cancel')}</Button>
            {held === null && shown === 'review' && <Button onClick={() => setStage('edit')}>{tc('back')}</Button>}
            {held === null && shown === 'edit' && (
              <Button variant="contained" onClick={review} disabled={!ops.canReceive || !storageOk}>
                {t('hold.review')}
              </Button>
            )}
            {held === null && shown === 'review' && (
              <Button
                variant="contained"
                color={body?.decision === 'reject' ? 'warning' : 'primary'}
                onClick={() => void confirm()}
                disabled={pending || !ops.canReceive || !storageOk}
              >
                {t(body?.decision === 'reject' ? 'hold.confirmReject' : 'hold.confirmAccept')}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
